// Server-side GTM (sGTM) client for the landing page.
//
// Mirrors how the PLATFORM sends conversion events (xDate frontend
// lib/gtm/gtm.ts `trackGtmEvent`): it POSTs to the backend `POST /gtm/events`,
// which forwards to the server-side GTM container (`/mp/collect`). We add this so
// the in-place paywall_popup flow emits the SAME events the regular (platform)
// paywall sends — server-side — so the paywall_popup cohort shows up in the same
// GA4 funnel and the paywall experiment can be measured.
//
// Reachability (verified against the backend): `POST /gtm/events` is guarded only
// by JwtOptionalAuthGuard (no api-key, no global guard) and CORS is `origin:true`,
// so an anonymous cross-origin POST from the LP succeeds. IMPORTANT: that guard
// reads the JWT ONLY from the `access_token` cookie, which a cross-origin LP can't
// set — so there is NO server-side user_id/email/click-id enrichment. Events carry
// `client_id` + `params` only. (We still accept an accessToken for forward-compat,
// but it is inert on this route today.)

import { getLanderContent, getPaywallNavArm } from './analytics';
import { PAYWALL_NAV_CONFIG_FLAG } from './authType';
import { getStorage } from './storage';
import { getEfTransactionId } from './everflow';
import { getLpSource } from '../config/domainConfig';

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`;
const API_KEY = process.env.REACT_APP_API_KEY;

// Mirrors analytics.js / api.js. Resolved from the live hostname
// (swipeyconnect vs swipeyquiz) — see src/config/domainConfig.js.
const LP_SOURCE = getLpSource();

// GA4 client_id from the first-party `_ga` cookie (`GA1.1.<n1>.<n2>` → `<n1>.<n2>`).
// The LP is GTM-only (no gtag / measurement id), so the cookie is the reliable
// source. Returns null if GA4 hasn't set it yet (consent-blocked / pre-load).
export const getGaClientId = () => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(/_ga=GA\d+\.\d+\.(\d+\.\d+)/);
  return m ? m[1] : null;
};

const genEventId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the timestamp-based id
  }
  return `${Date.now()}.${Math.random().toString(36).slice(2)}`;
};

// Shared attribution stamped on every event, matching analytics.js trackEvent.
const getAttribution = () => ({
  lp_source: LP_SOURCE,
  'lander-content': getLanderContent(),
  // Same key and same resolved value as the client dataLayer events, so both
  // halves of the funnel are splittable by the same cohort dimension.
  [PAYWALL_NAV_CONFIG_FLAG]: getPaywallNavArm(),
});

// Standard UTM keys pulled from the stored utm_data blob (setStorage('utm_data')
// in api.js). We pick the known keys instead of spreading the whole blob so the
// event isn't polluted with `path`/arbitrary URL params.
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_campaign_id',
  'utm_paytype',
];

// The stored UTM set + the Everflow transaction id — the page-level attribution.
// Exported so the CLIENT dataLayer registration_started (analytics.js) can carry
// the EXACT same set as the sGTM copy, not just a subset.
export const getUtmAndEf = () => {
  const utm = getStorage('utm_data') || {};
  const utmPicked = UTM_KEYS.reduce((acc, key) => {
    if (utm[key] != null && utm[key] !== '') acc[key] = utm[key];
    return acc;
  }, {});
  const ef = getEfTransactionId();
  return {
    ...utmPicked,
    ...(ef ? { ef_transaction_id: ef } : {}),
  };
};

// The FULLER attribution set, mirroring the backend's registration_started
// Mixpanel props (auth.service.ts processRegisterExternalPrelander): the UTM set
// plus the affiliate/publisher params and a resolved single `click_id`. Sourced
// from the stored `utm_data` blob, which snapshots ALL landing-URL params (see
// api.js collectAndStoreUTMData), so cmpid/pubid/pubalias/click_id are available
// here even though getUtmAndEf() (GTM path) intentionally narrows to UTM keys.
// Blank/undefined values are dropped so the Mixpanel event/profile isn't polluted.
const ATTRIBUTION_KEYS = [
  ...UTM_KEYS,
  'cmpid',
  'pubid',
  'pubalias',
];

export const getRegistrationAttribution = () => {
  const utm = getStorage('utm_data') || {};
  const ef = getEfTransactionId();
  const picked = ATTRIBUTION_KEYS.reduce((acc, key) => {
    if (utm[key] != null && utm[key] !== '') acc[key] = utm[key];
    return acc;
  }, {});
  // Backend resolves click_id from click_id → clickid → ef_transaction_id.
  const clickId = utm.click_id || utm.clickid || ef || null;
  return {
    ...picked,
    ...(clickId ? { click_id: clickId } : {}),
    ...(ef ? { ef_transaction_id: ef } : {}),
  };
};

// Everything the platform's server events carry beyond the raw event fields:
// lp_source + lander-content (getAttribution) + the UTM/ef set. email + click-ids
// vary per call site (only known there — see createAccount/PaywallPopup), so those
// are passed in, not read here.
const getBaseEnrichment = () => ({
  ...getUtmAndEf(),
  ...getAttribution(),
});

// POST one event to the backend sGTM forwarder. Fire-and-forget; never throws.
// Skips the send when there's no GA client_id yet — mirrors the platform, which
// only dispatches the server hit when a client_id is available (the client-side
// dataLayer event still fires separately via analytics.js). `keepalive` lets the
// request survive the imminent redirect to the Upgate hosted checkout page.
const sendServerGtmEvent = (name, params = {}, { accessToken } = {}) => {
  try {
    const client_id = getGaClientId();
    if (!client_id) return false;

    const headers = {
      'Content-Type': 'application/json',
      'x-api-key': API_KEY, // ignored by this route; harmless
    };
    // Inert today (the route reads the JWT only from the access_token cookie) —
    // kept for forward-compat if /gtm/events ever accepts a Bearer token.
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    fetch(`${API_BASE_URL}/gtm/events`, {
      method: 'POST',
      headers,
      keepalive: true,
      // Body shape must match SendGTMEventDto exactly — the backend ValidationPipe
      // (whitelist:true) strips any extra top-level keys.
      body: JSON.stringify({
        client_id,
        event_id: genEventId(),
        events: [{ name, params }],
      }),
    }).catch(() => {});
    return true;
  } catch (err) {
    console.error('[serverGtm] send failed', err);
    return false;
  }
};

// --- Thin wrappers mirroring the platform's exact event names + param shapes ---

// registration-started (platform gtm.ts trackGTMRegistrationStarted).
// `email` + `clickIds` (getExternalClickIds() map: gclid/fbclid/twclid/ttclid)
// are passed from the call site so the event carries the same rich attribution
// the platform's backend adds to its GTM events. Google has no email at click.
export const trackServerRegistrationStarted = ({
  flow,
  source,
  paywallType,
  paywallOrder,
  email,
  clickIds,
  accessToken,
} = {}) =>
  sendServerGtmEvent(
    'registration-started',
    {
      ...(flow ? { flow } : {}),
      ...(source ? { source } : {}),
      'paywall-type': paywallType,
      // popup arm the user saw ('after_signup' | 'popup_first'), omitted elsewhere.
      ...(paywallOrder ? { 'paywall-order': paywallOrder } : {}),
      ...(email ? { email } : {}),
      ...(clickIds || {}),
      ...getBaseEnrichment(),
    },
    { accessToken },
  );

// order-created (platform gtm.ts trackGTMOrderCreated). `item` mirrors the
// platform GTMPurchasedItem shape. `email` + `clickIds` as above.
export const trackServerOrderCreated = ({
  item,
  paywallType,
  paywallOrder,
  email,
  clickIds,
  accessToken,
} = {}) =>
  sendServerGtmEvent(
    'order-created',
    {
      items: [item],
      'paywall-type': paywallType,
      ...(paywallOrder ? { 'paywall-order': paywallOrder } : {}),
      ...(email ? { email } : {}),
      ...(clickIds || {}),
      ...getBaseEnrichment(),
    },
    { accessToken },
  );

// NOTE: registration-completed and subscription-purchase are intentionally NOT
// sent from the LP. subscription-purchase happens after payment, off the LP (on
// the platform /lobby). Google's registration events are owned by the backend
// (emitted at the OAuth callback). The LP only owns registration-started +
// order-created for the paywall_popup cohort.

export default {
  getGaClientId,
  trackServerRegistrationStarted,
  trackServerOrderCreated,
};
